import { FC, useCallback, useMemo } from 'react';
import { cn } from '@hqo/react-hooks';
import { MultiSelectBase, MultiSelectProps, useMultiSelect } from '@hqo/multi-select';

export interface HostFilterMultiSelectProps extends MultiSelectProps {
  showSelectAll?: boolean;
}

export const HostFilterMultiSelect: FC<HostFilterMultiSelectProps> = ({
  placeholder,
  maxCount = 3,
  id,
  className,
  modalPopover = false,
  selectedOptions = [],
  options,
  disabled,
  loading,
  onSearch,
  searchValue,
  onChange,
  onBlur,
  error,
  variant,
  moreLabel,
  selectAllLabel,
  clearLabel,
  closeLabel,
  noItemFoundLabel,
  searchPlaceholder = 'Search',
  hideMoreDeselect = false,
  onOpenChange,
  canClear = true,
  canClose = true,
  popoverClassName,
  popoverAlign = 'start',
  onFetchMore,
  isFetchingMore,
  hasMore,
  showSelectAll = true,
}: HostFilterMultiSelectProps) => {
  const { onClear, onClearAllOptions, onClearExtraOptions, onRemoveOption, toggleAll, onToggleOption } = useMultiSelect(
    {
      selectedOptions,
      options,
      maxCount,
      onSearch,
      onChange,
    },
  );
  const hasSelectedValues = selectedOptions.length > 0;
  const hasClearOrCloseActions = canClear || canClose;
  const selectedValuesSet = useMemo(() => new Set(selectedOptions.map((option) => option.value)), [selectedOptions]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && onBlur) {
        onBlur();
      }
      onOpenChange?.(isOpen);
    },
    [onBlur, onOpenChange],
  );

  return (
    <div className="w-full">
      <MultiSelectBase.Wrapper modal={modalPopover} onOpenChange={handleOpenChange}>
        <MultiSelectBase.Button
          id={id}
          className={cn(className, { 'border-destructive': error && !disabled })}
          data-testid="multi-select-button"
          disabled={disabled}
        >
          <MultiSelectBase.Value
            selectedOptions={selectedOptions}
            maxCount={maxCount}
            variant={variant}
            moreLabel={moreLabel}
            placeholder={placeholder}
            onClearAllOptions={onClearAllOptions}
            onClearExtraOptions={onClearExtraOptions}
            onRemoveOption={onRemoveOption}
            hideMoreDeselect={hideMoreDeselect}
          />
        </MultiSelectBase.Button>
        <MultiSelectBase.Popover className={popoverClassName} align={popoverAlign}>
          {onSearch && (
            <MultiSelectBase.Search onValueChange={onSearch} value={searchValue} placeholder={searchPlaceholder} />
          )}
          <MultiSelectBase.Options
            isLoading={loading}
            options={options}
            selectedValuesCount={selectedOptions.length}
            selectAllLabel={selectAllLabel}
            noItemFoundLabel={noItemFoundLabel}
            onToggleAll={showSelectAll ? toggleAll : undefined}
            onFetchMore={onFetchMore}
            isFetchingMore={isFetchingMore}
            hasMore={hasMore}
          >
            {(groupOptions, groupLabel) => (
              <MultiSelectBase.OptionsGroup
                groupLabel={groupLabel}
                options={groupOptions}
                selectedValuesSet={selectedValuesSet}
                onSelectOption={onToggleOption}
                OptionComponent={MultiSelectBase.Option}
              />
            )}
          </MultiSelectBase.Options>
          {hasSelectedValues && hasClearOrCloseActions && (
            <MultiSelectBase.Actions
              onClear={onClear}
              clearLabel={clearLabel}
              closeLabel={closeLabel}
              canClear={canClear}
              canClose={canClose}
            />
          )}
        </MultiSelectBase.Popover>
      </MultiSelectBase.Wrapper>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
